import React from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { EyeFill, PlayFill, PlusLg, Search, TrashFill } from 'react-bootstrap-icons';

import { AdoTestStatusOverride, RequirementWorkspaceTestCase } from '../../types/requirements';
import { formatFolderPathForDisplay } from './FolderPathSelector';

interface TestResultsListProps {
  tests: RequirementWorkspaceTestCase[];
  totalTestCount: number;
  selectedFolderName?: string;
  selectedTestKey?: string | null;
  selectedTestKeys: ReadonlySet<string>;
  selectedCount: number;
  allVisibleSelected: boolean;
  search: string;
  statusFilter: string;
  lastRunFilter: string;
  sortBy: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onLastRunFilterChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onSelectTest: (testCase: RequirementWorkspaceTestCase) => void;
  onToggleTestSelection: (testCase: RequirementWorkspaceTestCase) => void;
  onToggleVisibleSelection: () => void;
  onClearSelection: () => void;
  onMoveTest?: (testCase: RequirementWorkspaceTestCase) => void;
  onPreviewTest?: (testCase: RequirementWorkspaceTestCase) => void;
  onDeleteTest?: (testCase: RequirementWorkspaceTestCase) => void;
  onRunTests?: () => void;
  onAddTestCase?: () => void;
  showAdoActions?: boolean;
  adoPushInProgress?: 'selected' | 'all' | null;
  adoStatusOverrides?: Record<string, AdoTestStatusOverride>;
  onPushSelectedToAdo?: () => void;
  onPushAllToAdo?: () => void;
  showStatusFilter?: boolean;
  showLastRunFilter?: boolean;
  extraActions?: React.ReactNode;
}

function renderStatusLabel(status: string): string {
  if (!status) {
    return 'Never Run';
  }
  return status.replace(/_/g, ' ').replace(/\b\w/g, (value) => value.toUpperCase());
}

function getAdoStatusOverride(
  testCase: RequirementWorkspaceTestCase,
  adoStatusOverrides?: Record<string, AdoTestStatusOverride>,
): AdoTestStatusOverride | undefined {
  return testCase.test_key ? adoStatusOverrides?.[testCase.test_key] : undefined;
}

function renderAdoStatusLabel(
  testCase: RequirementWorkspaceTestCase,
  adoStatusOverrides?: Record<string, AdoTestStatusOverride>,
): string {
  const override = getAdoStatusOverride(testCase, adoStatusOverrides);
  if (override?.ado_status === 'missing') {
    const fallbackId = override.ado_work_item_id ?? testCase.ado_work_item_id;
    return override.label || (fallbackId ? `Not in ADO #${fallbackId}` : 'Not in ADO');
  }
  if (testCase.ado_push_status === 'missing') {
    if (testCase.ado_status_label) {
      return testCase.ado_status_label;
    }
    return testCase.ado_work_item_id ? `Not in ADO #${testCase.ado_work_item_id}` : 'Not in ADO';
  }
  if (testCase.ado_push_status === 'pushed' && testCase.ado_work_item_id) {
    return `ADO #${testCase.ado_work_item_id}`;
  }
  if (testCase.ado_push_status === 'updated') {
    return 'Needs ADO Push';
  }
  if (testCase.ado_push_status === 'not_pushed') {
    return 'Not in ADO';
  }
  return renderStatusLabel(String(testCase.ado_push_status || 'default'));
}

function adoStatusClass(
  testCase: RequirementWorkspaceTestCase,
  adoStatusOverrides?: Record<string, AdoTestStatusOverride>,
): string {
  const override = getAdoStatusOverride(testCase, adoStatusOverrides);
  if (override?.ado_status === 'missing') {
    return 'ado_missing';
  }
  if (testCase.ado_push_status === 'missing') {
    return 'ado_missing';
  }
  return testCase.ado_push_status || 'default';
}

function TestResultsList({
  tests,
  totalTestCount,
  selectedFolderName,
  selectedTestKey,
  selectedTestKeys,
  selectedCount,
  allVisibleSelected,
  search,
  statusFilter,
  lastRunFilter,
  sortBy,
  onSearchChange,
  onStatusFilterChange,
  onLastRunFilterChange,
  onSortChange,
  onSelectTest,
  onToggleTestSelection,
  onToggleVisibleSelection,
  onClearSelection,
  onMoveTest,
  onPreviewTest,
  onDeleteTest,
  onRunTests,
  onAddTestCase,
  showAdoActions = false,
  adoPushInProgress = null,
  adoStatusOverrides,
  onPushSelectedToAdo,
  onPushAllToAdo,
  showStatusFilter = true,
  showLastRunFilter = true,
  extraActions,
}: TestResultsListProps): React.ReactElement {
  return (
    <div className="rrw-panel rrw-results-panel">
      <div className="rrw-panel-header">
        <h3 className="rrw-panel-title">{selectedFolderName || 'All Test Cases'}</h3>
        <p className="rrw-panel-subtitle">
          {tests.length} test{tests.length === 1 ? '' : 's'} in view
          {selectedCount > 0 ? ` • ${selectedCount} selected across this generation` : ' • No tests selected'}
        </p>
      </div>

      <div className="rrw-inline-actions">
        {onAddTestCase && (
          <Button variant="outline-success" size="sm" onClick={onAddTestCase}>
            <PlusLg size={12} className="me-1" />
            Add Test Case
          </Button>
        )}
        <Button variant="outline-secondary" size="sm" onClick={onToggleVisibleSelection} disabled={tests.length === 0}>
          {allVisibleSelected ? 'Clear All' : 'Select All'}
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={onClearSelection} disabled={selectedCount === 0}>
          Clear Selection
        </Button>
        {showAdoActions && (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onPushSelectedToAdo}
              disabled={selectedCount === 0 || adoPushInProgress !== null}
            >
              {adoPushInProgress === 'selected' ? 'ADO Push Running' : 'Push Selected to ADO'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onPushAllToAdo}
              disabled={totalTestCount === 0 || adoPushInProgress !== null}
            >
              {adoPushInProgress === 'all' ? 'ADO Push Running' : 'Push All to ADO'}
            </Button>
          </>
        )}
        {onRunTests && (
          <Button variant="primary" size="sm" onClick={onRunTests}>
            <PlayFill size={12} className="me-1" />
            Run Tests
          </Button>
        )}
        {extraActions}
      </div>

      <div className="rrw-filter-bar">
        <div className="rrw-search">
          <Search size={14} className="rrw-search-icon" />
          <input
            className="rrw-search-input"
            type="text"
            placeholder="Search tests, stories, or folders"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>

        {showStatusFilter && (
          <Form.Select
            className="rrw-filter-select"
            size="sm"
            value={statusFilter}
            onChange={(event) => onStatusFilterChange(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="never_run">Never run</option>
            <option value="running">Running</option>
            <option value="pending">Pending</option>
          </Form.Select>
        )}

        {showLastRunFilter && (
          <Form.Select
            className="rrw-filter-select"
            size="sm"
            value={lastRunFilter}
            onChange={(event) => onLastRunFilterChange(event.target.value)}
          >
            <option value="all">Any last run</option>
            <option value="active">Run exists</option>
            <option value="never">Never run</option>
            <option value="recent">Last 7 days</option>
          </Form.Select>
        )}

        <Form.Select
          className="rrw-filter-select"
          size="sm"
          value={sortBy}
          onChange={(event) => onSortChange(event.target.value)}
        >
          <option value="priority">Sort: Priority</option>
          <option value="latest">Sort: Latest Run</option>
          <option value="failure">Sort: Failures First</option>
          <option value="title">Sort: Title</option>
        </Form.Select>
      </div>

      <div className="rrw-results-list">
        {tests.length === 0 && (
          <div className="rrw-empty-panel">
            No tests match the current folder and filters.
          </div>
        )}

        {tests.map((testCase, index) => {
          const isSelected = selectedTestKey === testCase.test_key;
          const isChecked = selectedTestKeys.has(testCase.test_key);
          const renderKey = `${testCase.test_key || 'test'}:${testCase.id ?? index}`;

          return (
            <div
              key={renderKey}
              className={`rrw-test-card ${isSelected ? 'rrw-test-card-selected' : ''}`}
              onClick={() => onSelectTest(testCase)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectTest(testCase);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="rrw-test-card-header">
                <div className="rrw-test-card-leading">
                  <Form.Check
                    className="rrw-test-card-check"
                    type="checkbox"
                    checked={isChecked}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => onToggleTestSelection(testCase)}
                    aria-label={`Select ${testCase.title || testCase.test_key}`}
                  />

                  <div className="rrw-test-card-copy">
                    <div className="rrw-test-card-title">
                    {testCase.title || testCase.test_key}
                  </div>
                    <div className="rrw-test-card-path">{formatFolderPathForDisplay(testCase.folder_path || [])}</div>
                  </div>
                </div>


              </div>

              <div className="rrw-test-card-meta">
                <span>Priority {testCase.priority ?? 'N/A'}</span>
                <span>{testCase.test_type || 'Unknown'} test</span>
                <span>{testCase.linked_user_story?.title || 'No linked story'}</span>
                {testCase.source_reference && (
                  <span className="rrw-source-ref">
                    {testCase.source_reference.startsWith('ado:')
                      ? `Source: ADO #${testCase.source_reference.slice(4)}`
                      : `Source: ${testCase.source_reference}`}
                  </span>
                )}
                {testCase.version_status && (
                  <span className={`rrw-status-pill rrw-status-pill-${testCase.version_status}`}>
                    {renderStatusLabel(testCase.version_status)}
                  </span>
                )}
                {showAdoActions && testCase.ado_push_status && (
                  <span className={`rrw-status-pill rrw-status-pill-${adoStatusClass(testCase, adoStatusOverrides)}`}>
                    {renderAdoStatusLabel(testCase, adoStatusOverrides)}
                  </span>
                )}
              </div>

              <div className="rrw-test-card-footer">
                <div className="rrw-test-card-run">
                  {testCase.last_run?.created_at ? `Last run ${new Date(testCase.last_run.created_at).toLocaleString()}` : 'Never executed'}
                </div>

                <div className="rrw-inline-actions">
                  {onMoveTest && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onMoveTest(testCase);
                      }}
                    >
                      Move
                    </Button>
                  )}
                  {onPreviewTest && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onPreviewTest(testCase);
                      }}
                    >
                      <EyeFill size={12} className="me-1" />
                      Preview
                    </Button>
                  )}
                  {onDeleteTest && (
                    <Button
                      className="rrw-test-card-delete"
                      variant="outline-danger"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteTest(testCase);
                      }}
                      aria-label={`Delete ${testCase.title || testCase.test_key}`}
                      title="Delete test case"
                    >
                      <TrashFill size={12} />
                    </Button>
                  )}

                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TestResultsList;
